<template>
    <div class="activity-submissions-section">
        <p
            v-if="completed && !readOnly && latestUserSubmission && !isCorporate"
            class="has-text-subtle activity-submissions-section__completed"
        >
            Completed
            {{ formatDate(latestUserSubmission.created_at, 'LLL zz') }}
        </p>

        <!-- Corporate: light, flat-row submissions list (reference: corporate
             "Assignment Page" — eyebrow header + n/max count, hairline-divided rows,
             status pill, date, delete). PrimeVue Tag + Button. The per-file detail
             (downloads, comments, Turnitin) is the parent-provided #content slot. -->
        <div v-if="isCorporate && showMySubmissions" class="subs-corp">
            <div v-if="showHeader" class="subs-corp__head">
                <span class="subs-corp__eyebrow">My Submissions</span>
                <span v-if="corporateCountLabel" class="subs-corp__count">{{ corporateCountLabel }}</span>
            </div>

            <!-- Themed PrimeVue rule dividing the header from the list (replaces the
                 first row's top hairline). -->
            <Divider v-if="showHeader" class="subs-corp__divider" />

            <div v-if="submissionsLoading" class="subs-corp__loading">
                <skeleton-card :active="submissionsLoading" :content="{ display: skeletonType }" />
            </div>

            <template v-else>
                <div v-for="submission in userSubmissions" :key="submission.id" class="subs-corp__item">
                    <!-- File-inline rows (file submission): one row per file, status +
                         date + delete on the first; matches the reference exactly. -->
                    <template v-if="inlineFiles && submissionMedia(submission).length">
                        <div
                            v-for="(file, fileIndex) in submissionMedia(submission)"
                            :key="`${submission.id}-${fileIndex}`"
                            class="subs-corp__row"
                        >
                            <span
                                v-if="fileIndex === 0"
                                class="subs-corp__status"
                                :class="{ 'subs-corp__status--late': submission.is_late }"
                            >
                                <span class="subs-corp__dot" aria-hidden="true" />
                                {{ submission.is_late ? 'Late' : 'On-Time' }}
                            </span>
                            <span v-else class="subs-corp__status subs-corp__status--ghost" aria-hidden="true" />

                            <component :is="fileIcon(file)" class="subs-corp__ficon" :size="18" aria-hidden="true" />

                            <span class="subs-corp__name">{{ fileTitle(file) }}</span>

                            <span class="subs-corp__date">
                                {{ fileIndex === 0 ? formatDate(submission.created_at, 'LLL zz') : '' }}
                            </span>

                            <a
                                v-if="file.public_url"
                                :href="file.public_url"
                                :download="fileTitle(file)"
                                class="subs-corp__dl"
                                :aria-label="`Download ${fileTitle(file)}`"
                            >
                                <PhDownloadSimple :size="17" aria-hidden="true" />
                            </a>

                            <PrimeVueButton
                                v-if="fileIndex === 0 && canDelete(submission)"
                                text
                                rounded
                                severity="secondary"
                                aria-label="Delete submission"
                                class="subs-corp__delete"
                                @click="confirmDelete(submission)"
                            >
                                <template #icon><PhTrash :size="16" /></template>
                            </PrimeVueButton>
                            <span v-else class="subs-corp__delete subs-corp__delete--ghost" aria-hidden="true" />
                        </div>

                        <div v-if="submissionIsGraded(submission) && hasMoreDetail()" class="subs-corp__graderow">
                            <router-link :to="gradeLink">
                                <PrimeVueButton label="View Grade" size="small" severity="info" text />
                            </router-link>
                        </div>

                        <div v-if="hasMoreDetail(submission)" class="subs-corp__detail">
                            <slot name="content" :submission="submission" />
                        </div>
                    </template>

                    <!-- Default meta row (acknowledge info etc.): pill status + slotted
                         attachments below. -->
                    <template v-else>
                        <div class="subs-corp__row">
                            <Tag
                                :value="submission.is_late ? 'Late' : 'On-Time'"
                                :severity="submission.is_late ? 'danger' : 'success'"
                                rounded
                                class="subs-corp__pill"
                            />

                            <Tag
                                v-if="allowsMultipleSubmissions"
                                :value="`${submission.attempt} / ${maxAttempts}`"
                                severity="secondary"
                                rounded
                                class="subs-corp__pill"
                            />

                            <Tag
                                v-if="get(submission, 'attempt_authorizations', []).length > 0"
                                value="Authorized Attempt"
                                severity="warn"
                                rounded
                                class="subs-corp__pill"
                            />

                            <span class="subs-corp__date">{{ formatDate(submission.created_at, 'LLL zz') }}</span>

                            <router-link
                                v-if="submissionIsGraded(submission) && hasMoreDetail()"
                                :to="gradeLink"
                                class="subs-corp__grade"
                            >
                                <PrimeVueButton label="View Grade" size="small" severity="info" outlined />
                            </router-link>

                            <PrimeVueButton
                                v-if="canDelete(submission)"
                                text
                                rounded
                                severity="secondary"
                                aria-label="Delete submission"
                                class="subs-corp__delete"
                                @click="confirmDelete(submission)"
                            >
                                <template #icon><PhTrash :size="16" /></template>
                            </PrimeVueButton>
                        </div>

                        <div v-if="hasMoreDetail(submission)" class="subs-corp__detail">
                            <slot name="content" :submission="submission" />
                        </div>
                    </template>
                </div>
            </template>
        </div>

        <div v-else-if="showMySubmissions" class="activity-submissions-stanbridge tw:flex tw:flex-col tw:gap-6">
            <div class="content activity-submissions-section__title">
                <h2 class="activity-peer-section-heading m-b-none tw:text-[var(--p-text-color)]!">My Submissions</h2>
            </div>

            <div v-if="submissions.length > 0 && !submissionsLoading" class="card-list is-compact">
                <div v-for="submission in userSubmissions" :key="submission.id" class="card">
                    <header class="card-header">
                        <div class="card-header-title">
                            <div class="tw:flex tw:w-full tw:flex-wrap tw:items-center tw:justify-start tw:gap-2">
                                <div class="tw:flex tw:items-center tw:gap-2">
                                    <div v-if="allowsMultipleSubmissions">
                                        <e-tag class="has-text-weight-normal">
                                            {{ submission.attempt }} /
                                            {{ maxAttempts }}
                                        </e-tag>
                                    </div>
                                    <div>
                                        <e-tag class="has-text-weight-normal">
                                            {{ formatDate(submission.created_at, 'LLL zz') }}
                                        </e-tag>
                                    </div>
                                </div>

                                <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
                                    <div v-if="submissionIsGraded(submission) && hasMoreDetail()">
                                        <e-button
                                            type="is-info"
                                            outlined
                                            tag="router-link"
                                            :to="gradeLink"
                                            size="is-small"
                                        >
                                            View Grade
                                        </e-button>
                                    </div>

                                    <div class="tw:flex tw:items-center tw:gap-2">
                                        <div v-if="get(submission, 'attempt_authorizations', []).length > 0">
                                            <e-tag type="is-warning" class="has-text-weight-normal">
                                                Authorized Attempt
                                            </e-tag>
                                        </div>
                                        <div>
                                            <div v-if="submission.is_late">
                                                <e-tag type="is-danger" class="has-text-weight-normal"> Late</e-tag>
                                            </div>

                                            <div v-else>
                                                <e-tag type="is-success" class="has-text-weight-normal"> On-Time</e-tag>
                                            </div>
                                        </div>
                                    </div>

                                    <div v-if="canDelete(submission)">
                                        <e-button type="is-danger is-outlined" @click="confirmDelete(submission)">
                                            <e-icon icon="trash-alt" />
                                        </e-button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </header>

                    <div v-if="hasMoreDetail(submission)" class="card-content">
                        <div class="content">
                            <slot name="content" :submission="submission" />
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="submissionsLoading">
                <skeleton-card :active="submissionsLoading" :content="{ display: skeletonType }" />
            </div>

            <p v-if="isEmpty(submissions) && !submissionsLoading" class="has-text-subtle m-b-none">
                No submissions to display.
            </p>
        </div>
    </div>
</template>

<script>
import { PhDownloadSimple, PhFile, PhFileDoc, PhFilePdf, PhFileXls, PhImage, PhTrash } from '@phosphor-icons/vue';
import find from 'lodash/find.js';
import get from 'lodash/get.js';
import PrimeVueButton from 'primevue/button';
import Divider from 'primevue/divider';
import Tag from 'primevue/tag';
import { useI18n } from 'vue-i18n';

import SkeletonCard from '@/components/skeleton-loader/skeleton-card/SkeletonCard.vue';
import { isCorporateClient } from '@/composables/useEdverumClient.js';
import { ACTIVITY_TYPES } from '@/constants/ActivityTypes.js';
import CourseActivityMixin from '@/mixins/CourseActivity.mixin.js';

export default {
    name: 'ActivitySubmissions',

    components: { PrimeVueButton, Divider, PhDownloadSimple, PhTrash, SkeletonCard, Tag },

    mixins: [CourseActivityMixin],

    props: {
        // Corporate file submission renders its own unified section header so the
        // uploader and list share one title; it passes false to suppress this one.
        showHeader: {
            type: Boolean,
            default: true,
        },

        // File submission renders each file inline in its row (icon · name · date ·
        // download · delete). Other corporate types leave files to the #content slot.
        inlineFiles: {
            type: Boolean,
            default: false,
        },
    },

    setup() {
        const { t: translate } = useI18n();
        return { translate };
    },

    data: () => ({
        // This component owns the initial submissions fetch via the immediate
        // activityId watcher below, so the mixin's created() fetch is skipped to
        // avoid a duplicate first-load request.
        ownsSubmissionsFetch: true,
    }),

    computed: {
        activityType() {
            return this.activity.activity_type;
        },

        isCorporate() {
            return isCorporateClient();
        },

        /**
         * Corporate header count ("2 / 3"). Only meaningful when the activity allows
         * multiple attempts; single-submission activities show no count.
         */
        corporateCountLabel() {
            if (!this.allowsMultipleSubmissions) {
                return null;
            }

            return `${this.userSubmissions.length} / ${this.maxAttempts}`;
        },

        /**
         * Controls visibility of the whole "My Submissions" block.
         *
         * Stanbridge keeps the existing behaviour (shown for graders / detailed types, with an
         * empty "No submissions to display." state). Corporate hides the block until the learner
         * has actually submitted something — so an unsubmitted activity doesn't render an empty,
         * low-value section. The skeleton is still allowed through while submissions are loading.
         */
        showMySubmissions() {
            const canView =
                this.$gate.allows('viewAny', ['ActivitySubmission', this.course]) ||
                this.$gate.allows('viewOther', ['ActivitySubmission', this.course]) ||
                this.hasMoreDetail();

            if (!canView) {
                return false;
            }

            if (this.isCorporate) {
                // The corporate list renders userSubmissions, so gate visibility on the
                // same set — submissions may include other learners' entries (viewOther),
                // which would show the header/count with no rows.
                return this.userSubmissions.length > 0 || this.submissionsLoading;
            }

            return true;
        },

        admin() {
            return this.$store.getters['User/hasRole']('Admin');
        },

        isActivityPastDue() {
            const dueAt = this.activity?.activity_settings?.due_at;

            if (!dueAt) {
                return false;
            }

            return this.$moment.utc(dueAt).isBefore(this.$moment.utc());
        },

        gradeLink() {
            return {
                name: 'student-grade-view',
                params: {
                    courseId: this.$route.params.courseId,
                    userId: this.$store.getters['Auth/userId'],
                },
            };
        },

        skeletonType() {
            return !['view-information', 'acknowledge-information'].some(
                check => check === this.activity.activity_type
            );
        },
    },

    watch: {
        // Refetch on every activity change. The mixin's created() only fetches when
        // the (global, never-cleared) submissions store is empty for this activity, so
        // on SPA navigation/revisits it skips the fetch and leaves submissionsLoading
        // stuck true — the list never renders until a full reload. Driving the fetch
        // off activityId guarantees fresh data and clears the loading flag each time.
        activityId: {
            immediate: true,
            handler(id) {
                if (!id || this.readOnly) {
                    this.submissionsLoading = false;
                    return;
                }

                this.submissionsLoading = true;
                this.fetchSubmissions();
            },
        },
    },

    methods: {
        get,

        submissionMedia(submission) {
            return get(submission, 'submittable.media', []) || [];
        },

        fileTitle(file) {
            return file.name || file.title || file.file_name || file.originalName || file.fileName || 'File';
        },

        // Returns the Phosphor icon component for a file's extension (used via
        // <component :is>). Same mapping the PrimeIcon version used, in Phosphor.
        fileIcon(file) {
            const ext = this.fileTitle(file).split('.').pop().toLowerCase();

            if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'heic'].includes(ext)) {
                return PhImage;
            }

            if (ext === 'pdf') {
                return PhFilePdf;
            }

            if (['doc', 'docx', 'rtf', 'txt'].includes(ext)) {
                return PhFileDoc;
            }

            if (['xls', 'xlsx', 'csv'].includes(ext)) {
                return PhFileXls;
            }

            return PhFile;
        },

        hasMoreDetail(submission = false) {
            if (this.activityType === ACTIVITY_TYPES.SURVEY) {
                return [ACTIVITY_TYPES.FILE_SUBMISSION, ACTIVITY_TYPES.SURVEY].includes(this.activityType);
            }

            if (submission && this.activityType === ACTIVITY_TYPES.QUIZ) {
                return (
                    this.activityType === ACTIVITY_TYPES.QUIZ && get(submission, 'submittable.quiz_attempt.total_score')
                );
            }

            if (this.activityType === ACTIVITY_TYPES.ACKNOWLEDGE_INFORMATION) {
                return submission && get(submission, 'submittable.media', []).length > 0;
            }

            return [ACTIVITY_TYPES.FILE_SUBMISSION, ACTIVITY_TYPES.LTI_TOOL].includes(this.activityType);
        },

        canDelete(submission) {
            if (this.isActivityPastDue) {
                return false;
            }

            if (this.submissionIsGraded(submission)) {
                return false;
            }

            const canDelete = this.$gate.allows('delete', ['ActivitySubmission', this.course]);

            const owner = this.isOwnSubmission(submission);

            if (this.activityType !== 'quiz') {
                return canDelete && owner;
            }

            return this.admin;
        },

        confirmDelete(submission) {
            this.$dialog.confirm({
                title: this.translate('deleting_submission'),
                message: this.translate('deleting_submission_confirmation'),
                confirmText: this.translate('delete'),
                type: 'is-danger',
                onConfirm: async () => {
                    this.submissionsLoading = true;

                    try {
                        const result = await this.$store.dispatch(
                            'ActivitySubmissions/deleteActivitySubmission',
                            submission
                        );

                        const isAxiosError = result instanceof Error;
                        const hasHttpError = result?.response?.status >= 400;
                        const hasApiError = result?.response?.data?.code >= 400;

                        if (isAxiosError || hasHttpError || hasApiError) {
                            const errorMessage =
                                result?.response?.data?.message ||
                                result?.message ||
                                'Unable to delete this submission. Please try again.';

                            this.$edverumToast.open({
                                duration: 5000,
                                message: errorMessage,
                                position: 'is-top',
                                type: 'is-danger',
                            });

                            this.submissionsLoading = false;
                            return;
                        }

                        await this.fetchSubmissions();
                        await this.$store.dispatch('Activities/fetchOne', this.activity.id);
                        this.verifyActivity();

                        this.$edverumToast.open({
                            duration: 3000,
                            message: 'Submission deleted successfully.',
                            position: 'is-top',
                            type: 'is-success',
                        });
                    } catch (error) {
                        const errorMessage =
                            error?.response?.data?.message || 'An error occurred while deleting the submission.';

                        this.$edverumToast.open({
                            duration: 5000,
                            message: errorMessage,
                            position: 'is-top',
                            type: 'is-danger',
                        });
                    } finally {
                        this.submissionsLoading = false;
                    }
                },
            });
        },

        isOwnSubmission(submission) {
            return Number(submission.created_by) === Number(this.$store.getters['Auth/userId']);
        },

        submissionIsGraded(submission) {
            return find(this.userGrades, userGrade => userGrade.activity_submission_id === submission.id);
        },
    },
};
</script>

<style scoped lang="scss">
@import 'resources/scss/toolbox.scss';

.activity-submissions-section {
    margin-bottom: 0;
    margin-top: 0;
}

.activity-submissions-stanbridge {
    > * {
        margin-bottom: 0 !important;
        margin-top: 0 !important;
    }
}

// Desktop/tablet (>= 769px) must render exactly as before the mobile spacing work
// (868kj17g6 AC-6, ADR-051 decision 5). The previous markup was a Bulma `.columns` stack
// inside a `tw:mb-6` root, so these rules restore its computed spacing on the flattened
// markup; the mobile layout above is unchanged.
//   - Root `tw:mb-6`: renderers reset the margin of this root (`> *` / `:deep()` with
//     !important), so the 1.5rem trailing space is restored as padding instead.
//   - "Completed …" line: separate `.columns` row, 1.5rem above the heading row.
//   - Heading row to list: two `.column` paddings ($column-gap each) in the multiline row.
//   - Heading size/weight come from the `.content` wrapper (`.content h2`), as before; the
//     shell's mobile `.content h2.activity-peer-section-heading` rule overrides it on mobile.
@media screen and (min-width: 769px) {
    .activity-submissions-section {
        padding-bottom: 1.5rem;
    }

    .activity-submissions-section__completed:not(:last-child) {
        margin-bottom: 1.5rem;
    }

    .activity-submissions-stanbridge {
        row-gap: $column-gap * 2;
    }
}

// Corporate "My Submissions" — light, flat, hairline-divided rows (reference design).
// Stanbridge keeps Bulma cards (markup above is unchanged), so all of this is scoped
// to .subs-corp and never touches the other client.
.subs-corp {
    display: flex;
    flex-direction: column;

    .subs-corp__head {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: 0.875rem;
        margin-bottom: 0;
    }

    // Header → list rule. Override PrimeVue's default 1.25rem block margins so it
    // sits tight under the header.
    .subs-corp__divider.p-divider {
        margin: 0.75rem 0 0.25rem;
    }

    // Section header matches the redesign convention shared by the Podcast / Quiz /
    // Acknowledge cards (#363636, ~1rem, weight 600, sentence case) rather than the
    // pale uppercase-tracked eyebrow it started as, so it reads as part of the page.
    .subs-corp__eyebrow {
        font-size: 1rem;
        font-weight: 600;
        color: #363636;
    }

    .subs-corp__count {
        font-size: 0.85rem;
        color: #94a3b8;
    }

    .subs-corp__item {
        border-top: 1px solid #f1f5f9;
    }

    // The Divider (or the parent's own divider, e.g. FileSubmissionCorporate) already
    // draws the line above the first row.
    .subs-corp__item:first-child {
        border-top: 0;
    }

    .subs-corp__row {
        display: flex;
        align-items: center;
        gap: 0.875rem;
        padding: 0.875rem 0;
    }

    // Inline file row (reference): dot + status text, file icon, name, date, download, trash.
    .subs-corp__status {
        display: inline-flex;
        flex: 0 0 auto;
        align-items: center;
        gap: 0.4rem;
        min-width: 5.25rem;
        font-size: 0.8rem;
        color: #047857;
    }

    .subs-corp__status--late {
        color: #b91c1c;
    }

    .subs-corp__status--ghost {
        min-width: 5.25rem;
    }

    .subs-corp__dot {
        width: 6px;
        height: 6px;
        border-radius: 9999px;
        background: #10b981;
    }

    .subs-corp__status--late .subs-corp__dot {
        background: #ef4444;
    }

    .subs-corp__ficon {
        flex: 0 0 auto;
        color: #64748b; // Phosphor svg fills with currentColor; size via :size prop.
    }

    .subs-corp__name {
        flex: 1 1 auto;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 0.875rem;
        color: #334155;
    }

    .subs-corp__date {
        flex: 0 0 auto;
        margin-left: auto;
        font-size: 0.8125rem;
        color: #94a3b8;
    }

    // Download: plain anchor (native download), blue icon that deepens on hover.
    .subs-corp__dl {
        display: inline-flex;
        flex: 0 0 auto;
        align-items: center;
        justify-content: center;
        color: #3b82f6;

        &:hover {
            color: #2563eb;
        }
    }

    // Quiet grey trash that warms to red on hover.
    .subs-corp__delete {
        flex: 0 0 auto;

        // Phosphor trash renders as an svg inside the PrimeVue #icon slot; color it
        // via currentColor (size is set by the :size prop).
        :deep(svg) {
            color: #cbd5e1;
            transition: color 0.15s ease;
        }

        &:hover :deep(svg) {
            color: #ff5e73;
        }
    }

    .subs-corp__delete--ghost {
        width: 2.25rem;
    }

    .subs-corp__grade {
        flex: 0 0 auto;
    }

    // Pill status/attempt (non-file types, e.g. acknowledge info): smaller + lighter.
    .subs-corp__pill {
        flex: 0 0 auto;
        font-size: 0.7rem;
        font-weight: 600;
        padding-block: 0.15rem;
    }

    .subs-corp__graderow {
        padding: 0 0 0.75rem;
    }

    // Comments / Turnitin injected by the parent via the #content slot.
    .subs-corp__detail {
        padding: 0 0 1rem;
    }
}

// Mobile: tighten the row so the filename keeps room (it ellipsizes); drop the
// fixed status width so short screens don't overflow.
@media screen and (max-width: 480px) {
    .subs-corp .subs-corp__row {
        gap: 0.5rem;
    }

    .subs-corp .subs-corp__status,
    .subs-corp .subs-corp__status--ghost {
        min-width: 0;
    }
}
</style>
